import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Scale, AlertCircle, Building2, Terminal, Loader2, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../api/client';
import debounce from 'lodash.debounce';

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState('');

  // Resolution State
  const [resolutionStatus, setResolutionStatus] = useState('idle'); // idle, loading, success, error
  const [resolverMode, setResolverMode] = useState('none'); // none, single, multiple
  const [resolvedTenantName, setResolvedTenantName] = useState('');
  const [availableTenants, setAvailableTenants] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  // Debounced Resolver
  const resolveTenant = async (emailInput) => {
    if (!emailInput || !emailInput.includes('@') || emailInput.length < 5) {
      setResolutionStatus('idle');
      setResolverMode('none');
      setTenantId('');
      return;
    }

    setResolutionStatus('loading');
    setError('');

    try {
      const res = await apiClient.get(`/auth/resolve-tenant?email=${encodeURIComponent(emailInput)}`);
      const data = res.data;

      if (data.mode === 'single') {
        setResolverMode('single');
        setResolvedTenantName(data.tenant.nombre);
        setTenantId(data.tenant.id);
        setResolutionStatus('success');
      } else if (data.mode === 'multiple') {
        setResolverMode('multiple');
        setAvailableTenants(data.tenants);
        setTenantId(''); // Force user to choose
        setResolutionStatus('success');
      }
    } catch (err) {
      console.warn("Tenant Resolution Failed", err);
      setResolverMode('none');
      setResolutionStatus('error');
      // We don't block login, but it will likely fail if tenant is required
      // unless user is super_admin or we have fallback
    }
  };

  // Create debounced version
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedResolve = useCallback(debounce(resolveTenant, 500), []);

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    debouncedResolve(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDebugInfo(null);
    setLoading(true);

    // Use the resolved tenantId if available, else let backend try auto-resolution
    const targetTenant = tenantId || "auto";
    const result = await login(email, password, targetTenant);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
      if (result.debug) setDebugInfo(result.debug);
    }

    setLoading(false);
  };

  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6">
            <div className="h-12 w-12 bg-gray-900 rounded-lg flex items-center justify-center mb-4">
              <Scale className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
              IMPI Monitor
            </h1>
            <p className="text-sm text-gray-500">
              Sistema de Monitoreo de Marcas
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex flex-col gap-2 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="login-error-message">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-800">{error}</span>
                </div>
                {debugInfo && (
                  <div className="mt-2 text-xs font-mono text-red-900 break-all">
                    Reason: {debugInfo.reason} {debugInfo.raw}
                  </div>
                )}
              </div>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={handleEmailChange}
                required
                className="h-10 bg-white border-gray-300 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                data-testid="login-email-input"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-10 bg-white border-gray-300 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-10 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-md transition-all active:scale-[0.98]"
              disabled={loading || (resolutionStatus === 'loading')}
              data-testid="login-submit-button"
            >
              {loading ? 'Iniciando...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <div className="mt-6 text-center text-xs text-gray-500">
            <p>Sistema Multi-Tenant · IMPI © 2025</p>
          </div>
        </div>
      </div>
    </div>
  );
};
